/* eslint-disable web/complete-jsdoc-v2 -- Documented at module level. */
/* eslint-disable web/require-heading-hierarchy -- The H1 is rendered by the hero section earlier in the tree; the static analyzer can't see through the imported component boundary. */

/**
 * Contact Page — PatrolPuffin Marketing Portal (Phase O.7 migration).
 *
 * Composed entirely of `@puffin-core/marketing-shell` primitives:
 *   `<MarketingSeo>`, `<ContactForm>`, `<ContactFormSuccess>` + the
 *   `createUseContactForm` hook. Server contract is unchanged — POSTs
 *   to `/api/v1/marketing/contact` (patrol-api's
 *   `@puffin-core/marketing-contact-routes` mount).
 *
 * @module portals/marketing-website/pages/ContactPage
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { getBrand } from '@puffin-core/brand-assets';
import { CaptchaField } from '@puffin-core/captcha-ui';
import { isEmailAddress } from '@puffin-core/email-address';
import {
  MarketingSeo,
  MarketingHero,
  ContactForm,
  ContactFormSuccess,
  createUseContactForm,
  createContactApiClient,
  buildContactOutcomeAdapter,
  useAnalytics,
  organization,
  webPage,
  normalizeContactPhone,
} from '@puffin-core/marketing-shell';
import { IS_CAPTCHA_REQUIRED } from '../config/turnstileConfig';
import { useCaptchaFieldSync } from '../hooks/useCaptchaFieldSync';

// MARKETING-CONTACT-CLIENT-001 (Wave 6.1): consume canonical
// `createContactApiClient` factory. patrol-api mounts the contact router
// via `@puffin-core/marketing-contact-routes` at `/v1/marketing/contact`.
const CONTACT_API_PATH = '/api/v1/marketing/contact';

const APP_KEY = 'patrol';
const CONTACT_TITLE = 'Contact Us - PatrolPuffin Carrier Invoice Audit';
const CONTACT_DESCRIPTION =
  'Ready to stop overpaying on carrier invoices? Contact PatrolPuffin for a demo of our automated freight audit and shipping cost recovery platform.';
const CONTACT_CANONICAL = 'https://patrolpuffin.com/contact';

function buildContactValidator(t) {
  return {
    // The Joi `.validate(values, options)` contract allows callers (the
    // `createUseContactForm` hook passes `{ abortEarly: false }`) to send a
    // second options argument; it is intentionally ignored here.
    async validate(values) {
      const details = [];
      // PHONE-001: '' when blank, E.164 when valid, null for a partial number (so a null always
      // means `values.phone` is a non-blank string, kept as typed for the error state).
      const phone = await normalizeContactPhone(values.phone);
      const trimmed = {
        businessName: (values.businessName || '').trim(),
        email: (values.email || '').trim().toLowerCase(),
        phone: phone ?? values.phone.trim(),
        monthlyShipments: (values.monthlyShipments || '').trim(),
        message: (values.message || '').trim(),
        captchaToken: values.captchaToken || null,
        website: values.website || '',
      };
      if (!trimmed.businessName) {
        details.push({
          path: ['businessName'],
          message: t('contact.validation.businessNameRequired'),
        });
      }
      if (!trimmed.email) {
        details.push({ path: ['email'], message: t('contact.validation.emailRequired') });
      } else if (!isEmailAddress(trimmed.email)) {
        details.push({ path: ['email'], message: t('contact.validation.emailInvalid') });
      }
      if (phone === null) {
        details.push({ path: ['phone'], message: t('contact.validation.phoneInvalid') });
      }
      if (IS_CAPTCHA_REQUIRED && !trimmed.captchaToken) {
        details.push({
          path: ['captchaToken'],
          message: t('contact.validation.captchaRequired'),
        });
      }
      if (details.length > 0) {
        return { error: { details }, value: trimmed };
      }
      return { value: trimmed };
    },
  };
}

// 🔴 AAS-I18N-002 ruling 42 (2026-08-10): the inline `buildContactApiAdapter` that lived here —
// byte-identical in seven marketing portals, plus a purchase-web module — is now
// `buildContactOutcomeAdapter` in `@puffin-core/marketing-shell`. Nine copies also meant nine
// copies of one bug: `if (status === 429) outcome = 'rate_limited'` short-circuited before the
// body was read, so the per-EMAIL limiter's translated `contact.errors.rate_limited_email` was
// unreachable in every app. The shared version resolves it from the `details.limiter` machine
// token instead.

const ValuePropItem = ({ title, description }) => (
  <div className="contact-value-prop-item">
    <h3 className="contact-value-prop-title">{title}</h3>
    <p className="contact-value-prop-description">{description}</p>
  </div>
);

ValuePropItem.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

const ValueProps = ({ t }) => {
  const items = t('contact.valueProps.items', { returnObjects: true });
  const list = Array.isArray(items) ? items : [];
  return (
    <div className="contact-page-info">
      <h2 className="contact-page-info-title">{t('contact.valueProps.title')}</h2>
      <div className="contact-value-props">
        {list.map((item) => (
          <ValuePropItem key={item.title} title={item.title} description={item.description} />
        ))}
      </div>
    </div>
  );
};

ValueProps.propTypes = {
  t: PropTypes.func.isRequired,
};

export default function ContactPage() {
  const [captchaToken, setCaptchaToken] = useState(null);

  // `getBrand` is a plain registry read (not a hook), so the jsonLd memo built
  // from it carries NO hook dependency and belongs ahead of the custom hooks,
  // per the canonical state → effect → computed → custom order. It is still
  // evaluated at RENDER time (not module load), so suites that stub
  // `@puffin-core/marketing-shell` keep resolving `organization` / `webPage`.
  const brand = getBrand(APP_KEY);

  const jsonLd = useMemo(
    () => [
      organization({
        appKey: APP_KEY,
        brand,
        description: CONTACT_DESCRIPTION,
      }),
      webPage({
        appKey: APP_KEY,
        brand,
        url: CONTACT_CANONICAL,
        title: CONTACT_TITLE,
        description: CONTACT_DESCRIPTION,
      }),
    ],
    [brand],
  );

  const { t } = useTranslation();
  const { trackPageView, trackEvent, track } = useAnalytics();

  useEffect(() => {
    trackPageView('/contact', CONTACT_TITLE);
  }, [trackPageView]);

  const useHook = useMemo(
    () =>
      createUseContactForm({
        validator: buildContactValidator(t),
        apiClient: buildContactOutcomeAdapter(
          createContactApiClient({ endpoint: CONTACT_API_PATH }),
          CONTACT_API_PATH,
        ),
        t,
        defaultValues: {
          businessName: '',
          email: '',
          phone: '',
          monthlyShipments: '',
          message: '',
          website: '',
        },
        onFirstInteract: () => {
          if (typeof track === 'function') {
            track('contact_form_started', { component: 'ContactPage', appKey: APP_KEY });
          }
        },
        onOutcome: ({ outcome, submissionId }) => {
          if (typeof trackEvent === 'function') {
            trackEvent('contact', outcome, submissionId || undefined);
          }
          if (outcome === 'success' && typeof track === 'function') {
            track('contact_form_submitted', { submissionId: submissionId || null });
          }
        },
        i18nNamespacePrefix: 'contact',
      }),
    [t, trackEvent, track],
  );
  const form = useHook();

  // Bridge: the captcha token lives outside the form-state machine
  // (mounted via the captchaSlot prop). Sync it into form.fields so the
  // validator + submit envelope pick it up.
  useCaptchaFieldSync(form.setField, captchaToken);

  const fields = useMemo(
    () => [
      {
        name: 'businessName',
        label: t('contact.form.businessName'),
        type: 'text',
        required: true,
        autoComplete: 'organization',
      },
      {
        name: 'email',
        label: t('contact.form.email'),
        type: 'email',
        required: true,
        autoComplete: 'email',
      },
      {
        name: 'phone',
        label: t('contact.form.phone'),
        type: 'phone',
        autoComplete: 'tel',
      },
      {
        name: 'monthlyShipments',
        label: t('contact.form.monthlyShipments'),
        type: 'select',
        options: [
          {
            value: '',
            label: t('contact.form.monthlyShipmentsPlaceholder'),
          },
          {
            value: 'under-500',
            label: t('contact.form.monthlyShipmentsOptions.under500'),
          },
          {
            value: '500-2000',
            label: t('contact.form.monthlyShipmentsOptions.500to2000'),
          },
          {
            value: '2000-plus',
            label: t('contact.form.monthlyShipmentsOptions.2000plus'),
          },
        ],
      },
      {
        name: 'message',
        label: t('contact.form.message'),
        type: 'textarea',
        rows: 5,
      },
    ],
    [t],
  );

  const ogImageUrl = `https://patrolpuffin.com${brand.assets.ogCard}`;

  const successContent = (
    <ContactFormSuccess
      title={t('contact.success.title')}
      body={t('contact.success.message')}
      submissionId={form.submissionId || undefined}
      submissionIdLabel={t('contact.success.referenceLabel', { defaultValue: 'Reference ID' })}
      resetLabel={t('contact.success.another')}
      onReset={form.reset}
    />
  );

  return (
    <>
      <MarketingSeo
        title={CONTACT_TITLE}
        description={CONTACT_DESCRIPTION}
        canonical={CONTACT_CANONICAL}
        ogType="website"
        ogImage={ogImageUrl}
        jsonLd={jsonLd}
      />

      <MarketingHero
        variant="centered"
        title={t('contact.hero.title')}
        subtitle={t('contact.hero.subtitle')}
      />

      <section className="section-marketing" id="contact-form">
        <div className="contact-page-layout">
          <ValueProps t={t} />
          <div className="contact-page-form">
            <ContactForm
              form={form}
              fields={fields}
              submitLabel={t('contact.form.submit')}
              submittingLabel={t('contact.form.submitting')}
              formAriaLabel={t('contact.form.ariaLabel', { defaultValue: 'Contact form' })}
              captchaSlot={
                IS_CAPTCHA_REQUIRED ? (
                  <CaptchaField
                    onVerify={setCaptchaToken}
                    onError={() => setCaptchaToken(null)}
                    onExpire={() => setCaptchaToken(null)}
                  />
                ) : null
              }
              successContent={successContent}
            />
          </div>
        </div>
      </section>
    </>
  );
}
